import { useEffect, useRef, useState } from 'react'

/** Hat der Browser eine Kamera (getUserMedia)? Sonst keinen Scan-Knopf zeigen. */
export function barcodeScanSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function'
}

/**
 * Kamera-Barcode-Scan via zxing-wasm (lazy importiert). Solange `active` true ist, läuft die Kamera und
 * dekodiert jedes Frame; beim ersten gültigen Treffer ruft es `onCode`. Reine Browser-/Kamera-Schnittstelle
 * — auf echtem Gerät geprüft, in jsdom/headless nicht lauffähig (daher kein automatisches Gate für DIESEN
 * Pfad; die Nummern-Suche, in die der Code mündet, ist dagegen voll getestet).
 */
export function useBarcodeScanner(active: boolean, onCode: (code: string) => void) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const onCodeRef = useRef(onCode)
  onCodeRef.current = onCode

  useEffect(() => {
    if (!active) return
    let stream: MediaStream | null = null
    let frame = 0
    let stopped = false
    const canvas = document.createElement('canvas')

    async function run(): Promise<void> {
      try {
        const { readBarcodes } = await import('zxing-wasm/reader')
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        const video = videoRef.current
        if (!video || stopped) return
        video.srcObject = stream
        await video.play()
        const tick = async (): Promise<void> => {
          if (stopped) return
          if (!video.videoWidth) {
            frame = requestAnimationFrame(() => void tick())
            return
          }
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const ctx = canvas.getContext('2d')
          if (!ctx) return
          ctx.drawImage(video, 0, 0)
          const results = await readBarcodes(ctx.getImageData(0, 0, canvas.width, canvas.height))
          const hit = results.find((result) => result.isValid && result.text)
          if (hit) {
            onCodeRef.current(hit.text)
            return
          }
          frame = requestAnimationFrame(() => void tick())
        }
        frame = requestAnimationFrame(() => void tick())
      } catch {
        setError('Kamera nicht verfügbar – bitte Teilenummer eintippen.')
      }
    }
    void run()

    return () => {
      stopped = true
      cancelAnimationFrame(frame)
      stream?.getTracks().forEach((track) => track.stop())
    }
  }, [active])

  return { videoRef, error }
}
